<script setup lang="ts">
import {
  LAWYER_TYPE_LAWYER,
  LAWYER_TYPE_ADVOCATE,
} from '@/shared/constants/lawyerTypeConstants';
import {
  ORGANIZATION_TYPE_CLUB,
  ORGANIZATION_TYPE_BUREAU,
} from '@/shared/constants/organizationTypeConstants';
import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';
import { buildCatalogUrl } from '@/shared/utils/buildCatalogUrl';
import { typeguardForString } from '@/shared/utils/typeguardForString';
import { useUserStore } from '@/entities/user/store';
import LawyerCardExperience from '@/widgets/LawyerCard/ui/LawyerCardExperience.vue';
import LawyerCardCost from '@/widgets/LawyerCard/ui/LawyerCardCost.vue';
import InfoName from '@/widgets/LawyerCard/ui/LawyerCardInfo/InfoName.vue';
import InfoConsultation from '@/widgets/LawyerCard/ui/LawyerCardInfo/InfoConsultation.vue';
import InfoLocation from '@/widgets/LawyerCard/ui/LawyerCardInfo/InfoLocation.vue';
import InfoStreetLink from '@/widgets/LawyerCard/ui/LawyerCardInfo/InfoStreetLink.vue';
import { CITY_IDS_FOR_ADDITIONAL_SPLIT } from '@/widgets/LawyerCard/lib/lawyerCardConstants';
import type { ILawyerCardInfoProps } from '@/widgets/LawyerCard/model/types/lawyerCard';
import type {
  ILawyerCardInfo,
  ILawyerAddress,
  ILawyerCompany,
  ISubwayMetro,
} from '@/entities/lawyer/model/types/lawyer';
import type { IUser } from '@/entities/user/model/types/user';

const props = withDefaults(defineProps<ILawyerCardInfoProps>(), {
  isLawyerPage: false,
  isCatalog: false,
  isCardSlider: false,
  isShowCost: false,
  isLawyerCompanyCard: false,
  isCompany: false,
  isArticle: false,
});

const route = useRoute();
const userStore = useUserStore();

const isMobile: boolean = useIsMobile();
const lawyerInfo = inject<ILawyerCardInfo>('lawyerInfo');

const user: ComputedRef<IUser | null> = computed(() => userStore.user);
const company: ComputedRef<ILawyerCompany | null | undefined> = computed(
  () => lawyerInfo?.company,
);

const addressItem: ComputedRef<ILawyerAddress | undefined> = computed(() => {
  if (lawyerInfo?.currentCompany) return lawyerInfo.currentCompany.lawyer.addresses?.[0];
  return lawyerInfo?.addresses?.[0];
});

const city: ComputedRef<string> = computed(() => addressItem.value?.city?.name || '');
const streetArr: ComputedRef<string[]> = computed(
  () => addressItem.value?.address?.split(',') || [],
);
const street: ComputedRef<string> = computed(() => {
  const spliceIndex = CITY_IDS_FOR_ADDITIONAL_SPLIT.includes(
    addressItem.value?.city?.id ?? -1,
  )
    ? 2
    : 1;
  return streetArr.value.length ? streetArr.value.slice(spliceIndex).join(',') : '';
});
const companyId: ComputedRef<number | null | undefined> = computed(
  () => lawyerInfo?.company_id,
);
const role: ComputedRef<number | undefined> = computed(() => lawyerInfo?.role);
const lawyerName: ComputedRef<string> = computed(
  () => `${lawyerInfo?.surname} ${lawyerInfo?.name} ${lawyerInfo?.middlename ?? ''}`,
);
const subway: ComputedRef<ISubwayMetro[]> = computed(
  () => addressItem.value?.metros || [],
);
const name: ComputedRef<string> = computed(() =>
  company.value ? company.value.name : lawyerName.value,
);
const separatorSymbol: ComputedRef<string> = computed(() =>
  !subway.value.length && !props.isLawyerPage && !props.isArticle ? ',' : '',
);

const userModeration = computed(() => user.value?.lawyer?.moderation);
const isRoleLawyer: ComputedRef<boolean> = computed(() =>
  userModeration.value?.role
    ? Number(userModeration.value.role) === LAWYER_TYPE_LAWYER
    : Number(user.value?.lawyer?.role) === LAWYER_TYPE_LAWYER,
);

const isConsultationAvailable: ComputedRef<boolean | undefined> = computed(
  () => lawyerInfo?.is_appointment_region || lawyerInfo?.is_appointment_online,
);

const linkToProfileCatalog: ComputedRef<string> = computed(() => {
  const region = addressItem.value?.city?.slug || '';
  const isIndividual =
    (role.value === LAWYER_TYPE_LAWYER || role.value === LAWYER_TYPE_ADVOCATE) &&
    !companyId.value;
  const isOrganization =
    (role.value === ORGANIZATION_TYPE_CLUB || role.value === ORGANIZATION_TYPE_BUREAU) &&
    companyId.value;

  if (!isIndividual && !isOrganization) return ROOT_CATALOG_URL;

  const type = isIndividual ? 'lawyers' : 'organizations';
  return buildCatalogUrl({ segments: ['geo', region, type, lawyerInfo?.slug] });
});

const removePageParam = (query: string): string => {
  const q = query.startsWith('?') ? query.slice(1) : query;
  const params = new URLSearchParams(q);
  params.delete('page');
  const result = params.toString();
  return query.startsWith('?') ? `?${result}` : result;
};

const linkCityCatalog: ComputedRef<string> = computed(() => {
  const sort = typeguardForString(route.params.sort);
  const category = typeguardForString(route.params.category);
  const subcategory = typeguardForString(route.params.subcategory);
  const region = addressItem.value?.city?.slug || '';
  const query = removePageParam(route.fullPath.split('?')[1] || '');

  if (subcategory && !category) return ROOT_CATALOG_URL;

  return buildCatalogUrl({
    segments: ['geo', region, category, subcategory, sort && 'sort', sort],
    query,
  });
});

const lawyerSpecializations: ComputedRef<string> = computed(() =>
  isRoleLawyer.value ? 'Юрист' : 'Адвокат',
);

defineExpose({ lawyerSpecializations });
</script>

<template>
  <div
    :class="[
      'lawyer-card-info',
      { _mobile: isMobile },
      { _catalog: isCatalog },
      { _slider: isCardSlider },
      { _lawyer: isLawyerPage },
      { _article: isArticle },
      { '_is-lawyer-company': isLawyerCompanyCard },
    ]"
  >
    <InfoName
      :name="name"
      :link-to-profile-catalog="linkToProfileCatalog"
      :is-lawyer-page="isLawyerPage"
      :is-catalog="isCatalog"
      :is-card-slider="isCardSlider"
      :is-lawyer-company-card="isLawyerCompanyCard"
      :is-article="isArticle"
    />
    <div v-if="!isLawyerCompanyCard && !isArticle" class="lawyer-card-info__experience">
      <LawyerCardExperience />
      <div
        v-if="isCatalog && !isLawyerCompanyCard && isShowCost"
        class="lawyer-card-info__cost-wrapper"
      >
        <LawyerCardCost />
      </div>
    </div>
    <InfoConsultation
      v-if="!isLawyerCompanyCard && isConsultationAvailable"
      :is-appointment-region="lawyerInfo?.is_appointment_region"
      :is-appointment-online="lawyerInfo?.is_appointment_online"
      :is-article="isArticle"
    />
    <InfoLocation
      v-if="!isLawyerCompanyCard"
      :city="city"
      :street="street"
      :separator-symbol="separatorSymbol"
      :link-city-catalog="linkCityCatalog"
      :subway="subway"
      :is-catalog="isCatalog"
      :is-card-slider="isCardSlider"
      :is-lawyer-page="isLawyerPage"
    />
    <InfoStreetLink v-if="isLawyerPage" :street="street" />
  </div>
</template>

<style lang="less">
.lawyer-card-info {
  &__experience {
    font-size: 14px;
    line-height: 21px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    margin-top: @main-size;
  }

  &._catalog &__experience {
    width: 100%;

    @media @tablet {
      display: flex;
      align-items: flex-start;
    }
  }

  &._mobile &__experience {
    font-size: @size-x3;
    line-height: @size-x4;
    margin-top: @size-x2;
  }
}
</style>
